import React, { useCallback, useEffect, useMemo, useState } from "react";
import Spinner from "../components/Spinner/Spinner";
import { formatDateWithDay } from "../utils/dateUtils";
import RichTextContent from "../components/Elements/RichTextContent";

const mapLinkForAddress = (address) =>
  address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "#";

const shuttleStatusLabel = (status, pickupSpotName) => {
  const spot = pickupSpotName || "pickup spot";
  const labels = {
    assigned: `Assigned to ${spot}`,
    on_route_to_pickup: `On route to ${spot}`,
    arrived_at_pickup: `Arrived at ${spot}`,
    departed_pickup: `Departed ${spot}`,
    arrived_at_event: `Arrived at event from ${spot}`,
    completed: "Completed",
  };

  return labels[status] || status;
};

const ParticipantShuttlePage = ({ uniqueCode }) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const [data, setData] = useState(null);
  const [pickupSpotId, setPickupSpotId] = useState("");
  const [messageForAdmin, setMessageForAdmin] = useState("");
  const [lateMinutes, setLateMinutes] = useState("5");
  const [showLateForm, setShowLateForm] = useState(false);
  const [showPickupReminder, setShowPickupReminder] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const request = useCallback(
    async (path, options = {}) => {
      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Shuttle request failed.");
      }
      return payload;
    },
    [apiBaseUrl],
  );

  const refresh = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      setError("");
      const payload = await request(`/api/participant-shuttle/${uniqueCode}`);
      setData(payload);
      if (!silent) {
        setPickupSpotId(payload.pickupSpot?.id || "");
        setMessageForAdmin(payload.participant?.messageForAdmin || "");
      }
    } catch (err) {
      setError(err.message || "Unable to load shuttle tracking.");
    } finally {
      setIsLoading(false);
    }
  }, [request, uniqueCode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === "visible") refresh({ silent: true });
    };
    const interval = setInterval(poll, 30000);
    document.addEventListener("visibilitychange", poll);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [refresh]);

  const participantStatus = data?.participant?.pickupCheckInStatus;
  const checkedInAt = data?.participant?.checkedInAt;

  useEffect(() => {
    setShowPickupReminder(false);
    if (!checkedInAt || participantStatus !== "waiting_at_pickup")
      return undefined;

    const checkedAtMs = new Date(checkedInAt).getTime();
    const reminderAt = checkedAtMs + 10 * 60 * 1000;
    const delay = Math.max(0, reminderAt - Date.now());
    const timer = setTimeout(() => setShowPickupReminder(true), delay);
    return () => clearTimeout(timer);
  }, [checkedInAt, participantStatus]);

  const shuttles = useMemo(() => data?.shuttles || [], [data]);
  const availablePickupSpots = useMemo(
    () => data?.availablePickupSpots || [],
    [data],
  );

  const submitRsvp = async (needRide) => {
    if (needRide === "Yes" && !pickupSpotId) {
      setError("Please choose a pickup spot.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");
      const payload = await request(
        `/api/participant-shuttle/${uniqueCode}/rsvp`,
        {
          method: "PUT",
          body: JSON.stringify({
            needRide,
            pickupSpotId: needRide === "Yes" ? Number(pickupSpotId) : null,
            messageForAdmin,
          }),
        },
      );
      setData(payload);
      setNotice(
        needRide === "Yes"
          ? "Your pickup spot is saved."
          : "Your RSVP is saved.",
      );
    } catch (err) {
      setError(err.message || "Could not save RSVP.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async (pickupCheckInStatus, extra = {}) => {
    try {
      setIsSaving(true);
      setError("");
      const payload = await request(
        `/api/participant-shuttle/${uniqueCode}/status`,
        {
          method: "PUT",
          body: JSON.stringify({ pickupCheckInStatus, ...extra }),
        },
      );
      setData(payload);
      setShowLateForm(false);
      setShowPickupReminder(false);
      setNotice("Status updated.");
    } catch (err) {
      setError(err.message || "Could not update status.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <Spinner />;
  if (error && !data) {
    return <div className="p-6 text-center text-red-600">{error}</div>;
  }

  const isPending = data.participant?.needRide === "Pending";
  const isCancelled = data.participant?.pickupCheckInStatus === "cancelled";

  return (
    <div
      className="min-h-screen bg-no-repeat bg-cover bg-center px-3 py-5"
      style={{ backgroundImage: "url('/background.png')" }}
    >
      <div className="mx-auto max-w-2xl space-y-4">
        <HeaderCard data={data} />

        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl bg-green-50 p-3 text-sm font-bold text-green-700">
            {notice}
          </div>
        )}

        {data.isEventExpired ? (
          <div className="rounded-2xl bg-white p-6 text-center font-bold text-red-600 shadow-lg">
            This event has ended.
          </div>
        ) : isPending ? (
          <RsvpCard
            data={data}
            pickupSpotId={pickupSpotId}
            setPickupSpotId={setPickupSpotId}
            messageForAdmin={messageForAdmin}
            setMessageForAdmin={setMessageForAdmin}
            submitRsvp={submitRsvp}
            isSaving={isSaving}
          />
        ) : isCancelled || data.participant?.needRide === "No" ? (
          <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
            <h2 className="text-xl font-black text-gray-900">Ride cancelled</h2>
            <p className="mt-2 text-sm text-gray-600">
              Admins will see that you cannot make it anymore.
            </p>
          </div>
        ) : (
          <>
            <PickupDetails data={data} />
            {showPickupReminder && (
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-lg">
                <h3 className="font-black text-indigo-950">
                  Have you been picked up?
                </h3>
                <p className="mt-1 text-sm text-indigo-900">
                  You checked in about 10 minutes ago. Tap yes if you are on the
                  shuttle.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    className="rounded-xl bg-green-600 px-3 py-2 text-sm font-bold text-white"
                    onClick={() => updateStatus("picked_up")}
                  >
                    Yes, picked up
                  </button>
                  <button
                    className="rounded-xl bg-white px-3 py-2 text-sm font-bold text-indigo-900"
                    onClick={() => setShowPickupReminder(false)}
                  >
                    Not yet
                  </button>
                </div>
              </div>
            )}
            {data.event?.shuttleDriverInfoVisible ? (
              <ShuttleCards
                shuttles={shuttles}
                pickupSpotName={data.pickupSpot?.name}
              />
            ) : (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950 shadow-lg">
                Driver and shuttle details will be shared here when they are
                available.
              </div>
            )}
            <StatusActions
              status={data.participant?.pickupCheckInStatus}
              isSaving={isSaving}
              showLateForm={showLateForm}
              setShowLateForm={setShowLateForm}
              lateMinutes={lateMinutes}
              setLateMinutes={setLateMinutes}
              updateStatus={updateStatus}
            />
            {data.event?.allowShuttlePickupSpotBrowsing &&
              availablePickupSpots.length > 0 && (
                <AlternatePickupSpots
                  spots={availablePickupSpots}
                  currentId={data.pickupSpot?.id}
                />
              )}
          </>
        )}
      </div>
    </div>
  );
};

const HeaderCard = ({ data }) => (
  <div className="rounded-3xl border border-white/60 bg-white/95 p-5 shadow-xl">
    {data.accountLogo && (
      <div className="mb-4 flex justify-center">
        <img
          src={data.accountLogo}
          alt="Account Logo"
          className="max-h-24 object-contain"
        />
      </div>
    )}
    <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
      Shuttle pickup
    </p>
    <h1 className="mt-1 text-2xl font-black text-gray-900">
      Hi, {data.participant?.name}
    </h1>
    <p className="mt-2 text-sm text-gray-600">{data.event?.title}</p>
    {data.event?.location && (
      <a
        className="mt-2 block text-sm font-semibold text-[#004a7e] underline decoration-[#004a7e]/30 underline-offset-2"
        href={mapLinkForAddress(data.event.location)}
        target="_blank"
        rel="noreferrer"
      >
        Destination Address: {data.event.location}
      </a>
    )}
    <p className="mt-1 text-sm font-semibold text-[#004a7e]">
      {formatDateWithDay(data.event?.dateOfOccurrence)}
    </p>
    {data.event?.eventMessageForParticipants && (
      <div className="mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-950">
        <RichTextContent html={data.event.eventMessageForParticipants} />
      </div>
    )}
  </div>
);

const RsvpCard = ({
  data,
  pickupSpotId,
  setPickupSpotId,
  messageForAdmin,
  setMessageForAdmin,
  submitRsvp,
  isSaving,
}) => (
  <div className="rounded-2xl bg-white p-4 shadow-lg">
    <h2 className="text-xl font-black text-gray-900">
      Choose your pickup spot
    </h2>
    {!data.event?.rsvpOn && (
      <p className="mt-2 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
        RSVPs are currently closed.
      </p>
    )}
    <p className="mt-2 text-sm text-gray-600">
      Select from the available pickup spots. You will not need to type a custom
      address.
    </p>
    <select
      className="mt-4 w-full rounded-xl border border-gray-300 px-3 py-3 text-sm"
      value={pickupSpotId}
      onChange={(e) => setPickupSpotId(e.target.value)}
      disabled={!data.event?.rsvpOn || isSaving}
    >
      <option value="">Choose pickup spot</option>
      {data.availablePickupSpots.map((spot) => (
        <option key={spot.id} value={spot.id}>
          {spot.name}
          {spot.pickupTime ? ` · ${spot.pickupTime}` : ""}
        </option>
      ))}
    </select>
    <input
      className="mt-3 w-full rounded-xl border border-gray-300 px-3 py-3 text-sm"
      placeholder="Message for admin, optional"
      value={messageForAdmin}
      onChange={(e) => setMessageForAdmin(e.target.value)}
      disabled={!data.event?.rsvpOn || isSaving}
    />
    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
      <button
        disabled={!data.event?.rsvpOn || isSaving}
        className="rounded-xl bg-green-600 px-4 py-3 text-sm font-black text-white disabled:bg-gray-300"
        onClick={() => submitRsvp("Yes")}
      >
        I need shuttle pickup
      </button>
      <button
        disabled={!data.event?.rsvpOn || isSaving}
        className="rounded-xl bg-red-100 px-4 py-3 text-sm font-black text-red-800 disabled:bg-gray-100"
        onClick={() => submitRsvp("No")}
      >
        I cannot make it
      </button>
    </div>
  </div>
);

const PickupDetails = ({ data }) => (
  <div className="rounded-2xl bg-white p-4 shadow-lg">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
          Your pickup spot
        </p>
        <h2 className="mt-1 text-xl font-black text-gray-900">
          {data.pickupSpot?.name || "Not assigned yet"}
        </h2>
        {data.pickupSpot?.pickupTime && (
          <div className="mt-3 inline-flex items-center rounded-2xl bg-[#004a7e] px-4 py-2 text-white shadow-sm">
            <span className="text-xs font-black uppercase tracking-wide opacity-80">
              Pickup time
            </span>
            <span className="ml-2 text-lg font-black">
              {data.pickupSpot.pickupTime}
            </span>
          </div>
        )}
        {data.pickupSpot?.address && (
          <div className="mt-3">
            <p className="text-xs font-black uppercase tracking-wide text-gray-500">
              Pickup Address:
            </p>
            <a
              className="mt-1 block text-sm font-bold text-[#004a7e] underline decoration-[#004a7e]/30 underline-offset-2"
              href={mapLinkForAddress(data.pickupSpot.address)}
              target="_blank"
              rel="noreferrer"
            >
              {data.pickupSpot.address}
            </a>
          </div>
        )}
      </div>
    </div>
  </div>
);

const StatusActions = ({
  status,
  isSaving,
  showLateForm,
  setShowLateForm,
  lateMinutes,
  setLateMinutes,
  updateStatus,
}) => {
  const statusButtonClass = (buttonStatus, activeClass, inactiveClass) =>
    `rounded-xl px-3 py-3 text-sm font-bold transition ${
      status === buttonStatus
        ? `${activeClass} ring-4 ring-offset-2 ring-[#004a7e]/25 shadow-lg scale-[1.02]`
        : inactiveClass
    }`;

  return (
    <div className="rounded-2xl bg-white p-4 shadow-lg">
      <h3 className="font-black text-gray-900">Update your status</h3>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          disabled={isSaving}
          className={statusButtonClass(
            "waiting_at_pickup",
            "bg-[#004a7e] text-white",
            "bg-[#004a7e]/10 text-[#004a7e]",
          )}
          onClick={() => updateStatus("waiting_at_pickup")}
        >
          I am at pickup location
        </button>
        <button
          disabled={isSaving}
          className={statusButtonClass(
            "picked_up",
            "bg-green-600 text-white",
            "bg-green-100 text-green-800",
          )}
          onClick={() => updateStatus("picked_up")}
        >
          I'm picked up
        </button>
        <button
          disabled={isSaving}
          className={statusButtonClass(
            "not_checked_in",
            "bg-gray-800 text-white",
            "bg-gray-100 text-gray-800",
          )}
          onClick={() => updateStatus("not_checked_in")}
        >
          Undo / not checked in
        </button>
        <button
          disabled={isSaving}
          className={statusButtonClass(
            "running_late",
            "bg-amber-500 text-white",
            "bg-amber-100 text-amber-900",
          )}
          onClick={() => setShowLateForm(!showLateForm)}
        >
          Running late
        </button>
      </div>
      {showLateForm && (
        <div className="mt-3 rounded-xl bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-950">
            The shuttle may or may not be able to wait for you.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              className="min-w-0 flex-1 rounded-lg border border-amber-300 px-3 py-2 text-sm"
              inputMode="numeric"
              value={lateMinutes}
              onChange={(e) => setLateMinutes(e.target.value)}
            />
            <button
              className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-bold text-white"
              onClick={() =>
                updateStatus("running_late", {
                  runningLateMinutes: Number(lateMinutes) || 1,
                })
              }
            >
              Send
            </button>
          </div>
        </div>
      )}
      <button
        disabled={isSaving || status === "cancelled"}
        className="mt-3 w-full rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm font-black text-red-800"
        onClick={() =>
          window.confirm("Tell admins you cannot make it anymore?") &&
          updateStatus("cancelled")
        }
      >
        I can't make it anymore
      </button>
    </div>
  );
};

const ShuttleCards = ({ shuttles, pickupSpotName }) => (
  <div className="space-y-3">
    <h3 className="rounded-2xl bg-white/95 px-4 py-3 font-black text-gray-900 shadow-lg">
      Assigned shuttle volunteers
    </h3>
    {shuttles.length === 0 && (
      <div className="rounded-2xl bg-white p-4 text-sm text-gray-600 shadow-lg">
        No shuttle has been assigned to this pickup spot yet.
      </div>
    )}
    {shuttles.map((shuttle) => (
      <div key={shuttle.id} className="rounded-2xl bg-white p-4 shadow-lg">
        <div className="mb-3 rounded-2xl bg-[#004a7e] px-4 py-3 text-white shadow-sm">
          <p className="text-[11px] font-black uppercase tracking-wide text-white/75">
            Shuttle status
          </p>
          <p className="text-base font-black">
            {shuttleStatusLabel(shuttle.status, pickupSpotName)}
          </p>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-[#004a7e]">
              Volunteer
            </p>
            <h4 className="mt-1 text-lg font-black text-gray-900">
              {shuttle.driverName || "Volunteer"}
            </h4>
            <p className="text-sm text-gray-600">
              {shuttle.vehicleDescription || "Vehicle description not set"}
            </p>
          </div>
          <div className="rounded-xl bg-indigo-50 px-3 py-2 text-center">
            <p className="text-lg font-black text-indigo-900">
              {shuttle.etaMinutes ? `${shuttle.etaMinutes}m` : "—"}
            </p>
            <p className="text-[10px] font-bold uppercase text-indigo-700">
              ETA
            </p>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const AlternatePickupSpots = ({ spots, currentId }) => (
  <div className="rounded-2xl bg-white p-4 shadow-lg">
    <h3 className="font-black text-gray-900">Other pickup spots</h3>
    <div className="mt-3 space-y-2">
      {spots
        .filter((spot) => spot.id !== currentId)
        .map((spot) => (
          <div key={spot.id} className="rounded-xl border border-gray-100 p-3">
            <p className="font-bold text-gray-900">{spot.name}</p>
            {spot.address && (
              <div className="mt-1">
                <p className="text-xs font-black uppercase tracking-wide text-gray-500">
                  Pickup Address:
                </p>
                <a
                  className="text-sm font-semibold text-[#004a7e] underline decoration-[#004a7e]/30 underline-offset-2"
                  href={mapLinkForAddress(spot.address)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {spot.address}
                </a>
              </div>
            )}
            <p className="mt-1 text-xs font-bold text-[#004a7e]">
              {spot.pickupTime
                ? `Pickup: ${spot.pickupTime}`
                : "Pickup time TBD"}
            </p>
          </div>
        ))}
    </div>
  </div>
);

export default ParticipantShuttlePage;
